import type { GradingScale } from '@dosfilos/domain';
import { gradePasses } from '../../../lib/grading';
import type { ActaDraftRowDto } from '../../../lib/api/grade.api';

// ─── El estado de una fila del acta ─────────────────────────────────────────
//
// EL ESTADO NO SE ELIGE: LO DICE LA NOTA. Antes era un desplegable, y aunque
// «Aprobado» y «Reprobado» venían deshabilitados, una lista con esas palabras
// se lee como algo que se escoge. Ahora el estado se MUESTRA, calculado con la
// misma escala que usa el servidor al cerrar (en LABTS: A–D aprueba, F
// reprueba), y la única decisión aparte es retirar a un alumno (W). Deshacer el
// retiro vuelve a lo que diga la nota.

/** Lo que se ve en la columna «Estado». */
export type ActaRowState = 'approved' | 'failed' | 'none' | 'withdrawn';
/** Lo único que se puede hacer con el estado. No existe «marcar reprobado». */
export type ActaRowAction = 'withdraw' | 'undoWithdraw';

/** Aprobado o reprobado según la escala; vacío si la nota todavía no se lee. */
export function statusOfGrade(
  grade: string,
  scale: GradingScale,
  coursePassing: number | null,
): '' | 'approved' | 'failed' {
  const passes = gradePasses({ label: grade.replace(',', '.') }, scale, coursePassing);
  return passes === null ? '' : passes ? 'approved' : 'failed';
}

export function actaRowView(input: {
  /** El estado de la inscripción en el servidor. */
  serverStatus: string;
  /** El del borrador en pantalla: `dropped` si se retiró acá. */
  draftStatus: string;
  grade: string;
  scale: GradingScale;
  passingGrade: number | null;
  closed: boolean;
}): { state: ActaRowState; actions: ActaRowAction[] } {
  // Retirado antes (desde la matrícula): se ve, pero no se deshace desde el acta.
  if (input.serverStatus === 'dropped') return { state: 'withdrawn', actions: [] };
  if (input.draftStatus === 'dropped') {
    return { state: 'withdrawn', actions: input.closed ? [] : ['undoWithdraw'] };
  }
  // Cerrada, manda lo que grabó el servidor.
  if (input.closed && (input.serverStatus === 'approved' || input.serverStatus === 'failed')) {
    return { state: input.serverStatus, actions: [] };
  }
  const byGrade = statusOfGrade(input.grade, input.scale, input.passingGrade);
  return {
    state: byGrade === '' ? 'none' : byGrade,
    actions: input.closed ? [] : ['withdraw'],
  };
}

/** LA COLUMNA «ASISTENCIA» SOLO SI LA SECCIÓN LLEVA ASISTENCIA. Una sección no
 *  tiene un interruptor para eso: lleva asistencia si alguien la tomó. Una
 *  columna entera de «—» en el acta de un curso sin asistencia es ruido. Si la
 *  consulta falló, la columna queda —con su aviso—: «no se pudo saber» no es
 *  «no hay». */
export function showAttendanceColumn(q: {
  isError: boolean;
  rows: ReadonlyArray<{ pct: number | null }> | undefined;
}): boolean {
  if (q.isError) return true;
  return (q.rows ?? []).some((r) => r.pct != null);
}

// ─── El borrador sin guardar ────────────────────────────────────────────────
//
// LAS NOTAS DEL ACTA SE OFICIALIZAN AL CERRARLA. Antes de eso se puede GUARDAR
// AVANCE (M1·G1): queda en el servidor, no es nota y nadie más que quien puede
// cerrar el acta lo ve. Lo escrito y todavía no guardado es un BORRADOR en la
// pantalla, y un borrador que se ve igual que una nota
// guardada engaña: el 2026-10-15 una prueba del selector (letras elegidas y no
// guardadas) se leyó como «notas importadas», y al volver a abrir el acta
// parecían haberse perdido. Un docente que escribe 8 notas y sale de la
// pantalla las pierde sin aviso. Por eso se compara el borrador con lo que
// trajo el servidor: lo que difiere está «sin guardar», se marca en su celda,
// se cuenta en la franja y se avisa antes de salir.

export interface DraftRow {
  grade: string;
  status: string;
}

/** Las filas cuyo borrador difiere de lo que trajo el servidor. */
export function unsavedRows(
  saved: Readonly<Record<string, DraftRow>>,
  draft: Readonly<Record<string, DraftRow>>,
): Set<string> {
  const out = new Set<string>();
  for (const [id, d] of Object.entries(draft)) {
    const s = saved[id];
    if (!s || s.grade.trim() !== d.grade.trim() || s.status !== d.status) out.add(id);
  }
  return out;
}

// ─── El avance guardado (M1·G1) ─────────────────────────────────────────────
// El servidor guarda una fila por inscripción, con su versión, quién y cuándo.
// La pantalla lo mezcla con lo que trae el libro: el avance manda sobre la
// propuesta del aula, y contra esa mezcla se mide qué está «sin guardar».

export type { ActaDraftRowDto };

export interface DraftMeta {
  rev: number;
  savedByName: string | null;
  savedAt: string;
}

/** El valor de una fila de avance como se escribe en la casilla. */
export function draftCellOf(r: Pick<ActaDraftRowDto, 'grade' | 'label' | 'status'>): DraftRow {
  const grade = r.label ?? (typeof r.grade === 'number' ? String(r.grade) : '');
  return { grade, status: r.status };
}

/** El borrador de la pantalla con el avance guardado encima, solo en las filas
 *  que siguen inscritas (`active`). Devuelve también la versión de cada fila. */
export function withSavedProgress(
  base: Readonly<Record<string, DraftRow>>,
  rows: readonly ActaDraftRowDto[],
  active: (enrollmentId: string) => boolean,
): { draft: Record<string, DraftRow>; meta: Record<string, DraftMeta> } {
  const draft: Record<string, DraftRow> = { ...base };
  const meta: Record<string, DraftMeta> = {};
  for (const r of rows) {
    if (!active(r.enrollmentId) || !(r.enrollmentId in base)) continue;
    draft[r.enrollmentId] = draftCellOf(r);
    meta[r.enrollmentId] = { rev: r.rev, savedByName: r.savedByName, savedAt: r.savedAt };
  }
  return { draft, meta };
}

/** Lo que viaja al «Guardar avance»: las filas sin guardar, con la versión con
 *  que se abrieron. Una casilla vacía (o ilegible) viaja sin nota: quita el
 *  avance de esa fila. */
export function progressPayload(
  ids: Iterable<string>,
  draft: Readonly<Record<string, DraftRow>>,
  meta: Readonly<Record<string, DraftMeta>>,
  gradeOf: (enrollmentId: string) => { grade?: number; label?: string } | null,
): Array<{
  enrollmentId: string;
  grade?: number;
  label?: string;
  status: 'approved' | 'failed' | 'dropped';
  baseRev: number | null;
}> {
  return [...ids].map((id) => {
    const status = (draft[id]?.status || 'approved') as 'approved' | 'failed' | 'dropped';
    return {
      enrollmentId: id,
      ...(status === 'dropped' ? {} : (gradeOf(id) ?? {})),
      status,
      baseRev: meta[id]?.rev ?? null,
    };
  });
}

/** «15:30» si fue hoy en el seminario; «16-10-2026 15:30» si fue otro día.
 *  `full` es el instante ya formateado con fecha y hora del seminario. */
export function savedWhen(full: string, savedDay: string, today: string): string {
  if (savedDay !== today) return full;
  return full.split(' ').slice(1).join(' ');
}
